import { z } from "zod";

import { ApiFetchFunction } from "@nens/ldo/auth-v1";

import { API_BASE_URL } from "../constants";

// Start an multipart upload by POSTing to get an upload id
async function createMultipartUpload(apiFetchFunction: ApiFetchFunction, excelImportId: number) {
  const response = await apiFetchFunction(
    `${API_BASE_URL}/excel-imports/${excelImportId}/files/files.zip/multipart-create`,
    {
      method: "POST",
    }
  );
  const { upload_id } = z.object({ upload_id: z.string() }).parse(response);

  return upload_id;
}

// Then for every chunk get an upload url and upload the chunk
async function uploadMultipartUpload(
  apiFetchFunction: ApiFetchFunction,
  excelImportId: number,
  uploadId: string,
  partNumber: number,
  fileChunk: Blob
) {
  // Get upload url
  const uploadResponse = await apiFetchFunction(
    `${API_BASE_URL}/excel-imports/${excelImportId}/files/files.zip/multipart-upload`,
    {
      method: "POST",
      body: JSON.stringify({ upload_id: uploadId, part_number: partNumber }),
    }
  );
  const { url } = z.object({ url: z.string() }).parse(uploadResponse);

  // Upload the chunk
  const response = await fetch(url, {
    method: "PUT",
    body: fileChunk,
  });

  return { partNumber, etag: response.headers.get("Etag") };
}

// Finish the multipart upload
async function finishMultipartUpload(
  apiFetchFunction: ApiFetchFunction,
  excelImportId: number,
  uploadId: string,
  parts: {
    partNumber: number;
    etag: string;
  }[]
) {
  try {
    await apiFetchFunction(
      `${API_BASE_URL}/excel-imports/${excelImportId}/files/files.zip/multipart-finish`,
      {
        method: "POST",
        body: JSON.stringify({
          upload_id: uploadId,
          parts: parts.map(({ partNumber, etag }) => ({ etag, part_number: partNumber })),
        }),
      }
    );
  } catch (e) {
    // Ignore trying to .json() the response error
  }
}

// Min and max as per https://docs.aws.amazon.com/AmazonS3/latest/userguide/qfacts.html
const MIN_CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_CHUNK_SIZE = 5 * 1024 * 1024 * 1024; // 5 GB
const MIN_AMOUNT_OF_PARTS = 1;
const MAX_AMOUNT_OF_PARTS = 10000;
function getBestChunkSize(fileSize: number): number {
  if (fileSize > MAX_AMOUNT_OF_PARTS * MAX_CHUNK_SIZE) throw Error("File is too big!");

  // Dynamically calculate the target number of parts based on file size
  let targetParts = Math.ceil(Math.log10(fileSize / (1024 * 1024)) * 10); // Logarithmic scaling
  targetParts = Math.max(targetParts, MIN_AMOUNT_OF_PARTS);
  targetParts = Math.min(targetParts, MAX_AMOUNT_OF_PARTS);

  let chunkSize = Math.ceil(fileSize / targetParts);
  chunkSize = Math.max(chunkSize, MIN_CHUNK_SIZE);
  chunkSize = Math.min(chunkSize, MAX_CHUNK_SIZE);

  return chunkSize;
}

export async function excelImportUploadMultipartZipfile(
  apiFetchFunction: ApiFetchFunction,
  excelImportId: number,
  file: File
) {
  if (!file.name.toLowerCase().endsWith(".zip"))
    throw new Error("excelImportUploadMultipartZipfile should only be used with ZIP files.");

  let cancelUploadId: string | null = null;
  try {
    const uploadId = await createMultipartUpload(apiFetchFunction, excelImportId);
    cancelUploadId = uploadId;

    const chunkSize = getBestChunkSize(file.size);
    const uploadPromises = new Array(Math.ceil(file.size / chunkSize))
      .fill(null)
      .map((_, index) => {
        const start = index * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const fileChunk = file.slice(start, end);
        const currentPartNumber = index + 1;

        return uploadMultipartUpload(
          apiFetchFunction,
          excelImportId,
          uploadId,
          currentPartNumber,
          fileChunk
        );
      });

    const parts = await Promise.all(uploadPromises);
    const filteredParts = parts.filter(
      (part): part is { etag: string; partNumber: number } => !!part.etag
    );

    if (filteredParts.length !== parts.length) {
      throw Error("No Etag in one or more of the partial uploads");
    }

    await finishMultipartUpload(apiFetchFunction, excelImportId, uploadId, filteredParts);
  } catch (e) {
    if (cancelUploadId) cancelMultipartUpload(apiFetchFunction, excelImportId, cancelUploadId);
    throw e;
  }
}

// If anything went wrong cancel the upload
async function cancelMultipartUpload(
  apiFetchFunction: ApiFetchFunction,
  excelImportId: number,
  uploadId: string
) {
  try {
    await apiFetchFunction(
      `${API_BASE_URL}/excel-imports/${excelImportId}/files/files.zip/multipart-cancel`,
      {
        method: "POST",
        body: JSON.stringify({ upload_id: uploadId }),
      }
    );
  } catch (e) {
    // Ignore trying to .json() the response error
  }
}
